

import React, { useContext } from 'react';

import { Island, PanelItemGroup } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { Circle } from '@r-components';
import { DisplayMode } from '@r-core';
import { DrawPanelItemGroupDimensions } from '../draw-dimensions';
import { RenderPanelItemGroup } from './render-panel-item-group';
import { renderTracking } from '@r-services/render-tracking';

type RenderPanelItemGroupsProps = {
	panelId: string;
	island: Island;
};

export const RenderPanelItemGroups = (props: RenderPanelItemGroupsProps): React.ReactElement => {
	const { controller: { options, navigation }} = useContext(AppContext);
	const { panelId, island } = props;

	renderTracking.incrementRender('ShowPanelItemGroups');

	const panel = island.panels.findById(panelId);
	const countertop = island.getCountertop()?.material;

	// something went wrong
	if (panel === null) return <></>;

	let { showAppliances, showCutouts } = options;
	switch (navigation.displayMode) {
		case DisplayMode.Manufacturing:
		case DisplayMode.InlineManufacturing:
			showAppliances = false;
			showCutouts = true;
			break;
		default:
	}

	const countertopThickness = countertop?.thickness ?? 0;
	// const overlaps = panel.getHorizontalIntersectionAreas(!showAppliances);

	return <g key={`panel-item-groups-${panelId}`} type="panel-item-groups-container">
		{
			panel.panelItemGroups.sortByCenter()
		.filter((panelItemGroup: PanelItemGroup) => {
			// In manufacturing views, skip groups containing only spacers
			if (navigation.displayMode === DisplayMode.Manufacturing || navigation.displayMode === DisplayMode.InlineManufacturing) {
				return panelItemGroup.items.children.some((pi) => !(pi.item.category?.isSpacer ?? false));
			}
			return true;
		})
		.sort((a, b) => {
			const aVent = a.hasVents ? 0 : 1;
			const bVent = b.hasVents ? 0 : 1;
			return aVent - bVent;
		})
		.map((panelItemGroup: PanelItemGroup) => {
				const groupOffset = {
					x: panelItemGroup.center.x,
					y: panelItemGroup.center.y
				};
				return <g
					key={panelItemGroup.id}
					type="panel-item-group"
					transform={`translate(${groupOffset.x}, ${groupOffset.y})`}
				>
					{ /* Dont bother if not rendering cutouts and appliances */ }
					{ (showAppliances || showCutouts) && <RenderPanelItemGroup panelItemGroupId={panelItemGroup.id}/> }
					{ (showAppliances || showCutouts) && !(panel.isSidePanel && options.isBlueprintView) && <DrawPanelItemGroupDimensions panelItemGroupId={panelItemGroup.id}/> }
					{ options.showDeveloperInsights &&
						<React.Fragment key={`dev-insights-${panelItemGroup.id}`}>
							<Circle
								x={0}
								y={0}
								r={0.25}
								fill="orange"
								tooltip={`appliance grouping origin (${groupOffset.x}, ${groupOffset.y})`}/>
							{ /* debug: Blue = Counter Origin */ }
							<rect
								x={-(panelItemGroup.maxWidth / 2)}
								y={-(panel.size.height / 2) - countertopThickness - panelItemGroup.getMaxDistanceAboveCountertop()}
								width={panelItemGroup.maxWidth}
								height={countertopThickness + panelItemGroup.getMaxDistanceAboveCountertop() + panel.size.height}
								fill="none"
								strokeWidth={0.125}
								stroke="blue"
								strokeDasharray="1 0.5 0.5"
							/>
						</React.Fragment>
					}
				</g>;
			})
		}
	</g>;
};
